/**
 * OPFS (Origin Private File System) Storage Module
 * 
 * Provides high-performance file storage for large model data.
 * OPFS is ideal for large binary files because:
 * - No size limits like localStorage/sessionStorage
 * - Better performance than IndexedDB for binary data
 * - Supports synchronous access from workers
 * - Persistent across sessions
 * 
 * File naming convention:
 * - Model data: `model-{modelId}.bin`
 * - Manifest: `manifest-{modelId}.json`
 */

const MODEL_FILE_PREFIX = 'model-';
const MODEL_FILE_SUFFIX = '.bin';
const MANIFEST_FILE_PREFIX = 'manifest-';
const MANIFEST_FILE_SUFFIX = '.json';

/**
 * Validate modelId before using it in an OPFS file name.
 *
 * Security note:
 * Even though OPFS is not a traditional filesystem, file handles are still looked up by name.
 * We reject unexpected characters to avoid path-like inputs and confusing edge cases.
 */
function assertSafeModelId(modelId: string): void {
  // Allow common ID formats (GUIDs, slugs): letters, numbers, dash, underscore, dot.
  // Disallow path separators and other special characters.
  if (!/^[A-Za-z0-9._-]+$/.test(modelId)) {
    throw new Error(`Invalid modelId for OPFS file name: ${modelId}`);
  }
}

/**
 * Gets the OPFS root directory.
 */
export async function getOPFSRoot(): Promise<FileSystemDirectoryHandle> {
  return navigator.storage.getDirectory();
}

/**
 * Gets the file name for a model's binary data.
 */
export function getModelFileName(modelId: string): string {
  assertSafeModelId(modelId);
  return `${MODEL_FILE_PREFIX}${modelId}${MODEL_FILE_SUFFIX}`;
}

/**
 * Gets the file name for a model's manifest.
 */
export function getManifestFileName(modelId: string): string {
  assertSafeModelId(modelId);
  return `${MANIFEST_FILE_PREFIX}${modelId}${MANIFEST_FILE_SUFFIX}`;
}

/**
 * Checks if OPFS is supported in this browser.
 */
export function isOPFSSupported(): boolean {
  return 'storage' in navigator && 'getDirectory' in navigator.storage;
}

/**
 * Checks if a model file exists in OPFS.
 */
export async function isModelCached(modelId: string): Promise<boolean> {
  try {
    const root = await getOPFSRoot();
    const fileName = getModelFileName(modelId);
    await root.getFileHandle(fileName);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets the size of a cached model file.
 * Returns null if the file doesn't exist.
 */
export async function getModelFileSize(modelId: string): Promise<number | null> {
  try {
    const root = await getOPFSRoot();
    const fileName = getModelFileName(modelId);
    const fileHandle = await root.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return file.size;
  } catch {
    return null;
  }
}

/**
 * Reads the entire model file from OPFS.
 * Returns null if the file doesn't exist.
 */
export async function readModelFile(modelId: string): Promise<ArrayBuffer | null> {
  try {
    const root = await getOPFSRoot();
    const fileName = getModelFileName(modelId);
    const fileHandle = await root.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return file.arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Writes model data to OPFS.
 * This overwrites any existing file.
 */
export async function writeModelFile(modelId: string, data: ArrayBuffer): Promise<void> {
  const root = await getOPFSRoot();
  const fileName = getModelFileName(modelId);
  const fileHandle = await root.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(data);
  } finally {
    await writable.close();
  }
}

/**
 * Writes model data to OPFS from multiple chunks.
 * Chunks are written sequentially to form a single file.
 * 
 * @param modelId - The model ID
 * @param chunks - Array of ArrayBuffers to concatenate and write
 * @param onProgress - Optional callback for progress updates
 */
export async function writeModelFileFromChunks(
  modelId: string,
  chunks: ArrayBuffer[],
  onProgress?: (written: number, total: number) => void
): Promise<void> {
  const root = await getOPFSRoot();
  const fileName = getModelFileName(modelId);
  const fileHandle = await root.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();

  const totalSize = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  let written = 0;

  try {
    for (const chunk of chunks) {
      await writable.write(chunk);
      written += chunk.byteLength;
      onProgress?.(written, totalSize);
    }
  } finally {
    await writable.close();
  }
}

/**
 * Deletes a model file from OPFS.
 */
export async function deleteModelFile(modelId: string): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const fileName = getModelFileName(modelId);
    await root.removeEntry(fileName);
  } catch {
    // File doesn't exist, ignore
  }
}

/**
 * Deletes all model files from OPFS.
 */
export async function clearAllModelFiles(): Promise<number> {
  const root = await getOPFSRoot();
  let count = 0;

  // @ts-expect-error - entries() is available but types may not reflect it
  for await (const [name] of root.entries()) {
    if (name.startsWith(MODEL_FILE_PREFIX) || name.startsWith(MANIFEST_FILE_PREFIX)) {
      await root.removeEntry(name);
      count++;
    }
  }

  return count;
}

/**
 * Lists all cached model IDs.
 */
export async function listCachedModels(): Promise<string[]> {
  const root = await getOPFSRoot();
  const modelIds: string[] = [];

  // @ts-expect-error - entries() is available but types may not reflect it
  for await (const [name] of root.entries()) {
    if (name.startsWith(MODEL_FILE_PREFIX) && name.endsWith(MODEL_FILE_SUFFIX)) {
      const modelId = name.slice(MODEL_FILE_PREFIX.length, -MODEL_FILE_SUFFIX.length);
      modelIds.push(modelId);
    }
  }

  return modelIds;
}

/**
 * Gets storage statistics for all cached models.
 */
export async function getStorageStats(): Promise<{
  modelCount: number;
  totalSize: number;
  models: Array<{ modelId: string; size: number; }>;
}> {
  const root = await getOPFSRoot();
  const models: Array<{ modelId: string; size: number; }> = [];
  let totalSize = 0;

  // @ts-expect-error - entries() is available but types may not reflect it
  for await (const [name, handle] of root.entries()) {
    if (name.startsWith(MODEL_FILE_PREFIX) && name.endsWith(MODEL_FILE_SUFFIX)) {
      const modelId = name.slice(MODEL_FILE_PREFIX.length, -MODEL_FILE_SUFFIX.length);
      const file = await (handle as FileSystemFileHandle).getFile();
      models.push({ modelId, size: file.size });
      totalSize += file.size;
    }
  }

  return {
    modelCount: models.length,
    totalSize,
    models,
  };
}

/**
 * Saves manifest data for a model.
 */
export async function saveManifest(modelId: string, manifest: object): Promise<void> {
  const root = await getOPFSRoot();
  const fileName = getManifestFileName(modelId);
  const fileHandle = await root.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(JSON.stringify(manifest));
  } finally {
    await writable.close();
  }
}

/**
 * Reads manifest data for a model.
 */
export async function readManifest<T = object>(modelId: string): Promise<T | null> {
  try {
    const root = await getOPFSRoot();
    const fileName = getManifestFileName(modelId);
    const fileHandle = await root.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    const text = await file.text();
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

/**
 * Deletes manifest for a model.
 */
export async function deleteManifest(modelId: string): Promise<void> {
  try {
    const root = await getOPFSRoot();
    const fileName = getManifestFileName(modelId);
    await root.removeEntry(fileName);
  } catch {
    // File doesn't exist, ignore
  }
}

